'use client'

import { useState } from 'react'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ChevronDown, RefreshCw, Search } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import type { RenderLink } from '@ui/web/utils'
import { BookModal } from '@ui/shared/lib'
import { Button, InputGroup, InputGroupAddon, InputGroupInput, Skeleton } from '@ui/web/components'
import { ReadBookIcon } from '@ui/web/icon'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../context'
import { Empty } from './empty'
import { NoResultFound } from './no-result-found'

interface LovedBookProps {
  type: 'web' | 'school'
  renderEmptyLink: RenderLink
  renderBookLink: RenderLink
}

export function LovedBook({ type, renderEmptyLink, renderBookLink }: LovedBookProps) {
  const trpc = useSharedTRPC()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, { wait: 800 })

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    ...trpc.loveBook.getAllLovedBooks.infiniteQueryOptions(
      {
        search: debouncedSearchTerm,
        pageSize: 10,
      } as Parameters<typeof trpc.loveBook.getAllLovedBooks.infiniteQueryOptions>[0],
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  })

  const allRows = data?.pages.flatMap((p) => p.data) ?? []

  return (
    <main className='srd:w-full srd:h-fit srd:min-h-[50dvh] srd:flex srd:flex-col srd:gap-y-8 srd:px-2'>
      <section className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:flex-wrap srd:gap-y-6'>
        <h1 className={cn('srd:md:text-2xl srd:text-xl srd:font-bold', type === 'web' && 'srd:text-neutrals-white')}>
          Buku Pilihanku
        </h1>

        <InputGroup className='srd:bg-neutrals-white srd:sm:max-w-[300px]'>
          <InputGroupAddon align='inline-start'>
            <Search className='text-main-blue-100 size-4' />
          </InputGroupAddon>
          <InputGroupInput
            placeholder='Cari judul buku...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isPending || isFetchingNextPage}
          />
        </InputGroup>
      </section>

      {(() => {
        if (isError) {
          return (
            <div
              className={cn(
                'srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:min-h-[40dvh] srd:text-center srd:py-10',
                type === 'web' && 'srd:text-neutrals-white',
              )}
            >
              <p className='srd:text-xl srd:font-semibold'>Oops, Terjadi Kesalahan</p>
              <p className='srd:text-sm srd:opacity-90'>Gagal memuat buku. Silakan coba lagi.</p>
              <Button
                onClick={() => void refetch()}
                className={cn(
                  'srd:px-6 srd:py-2 srd:rounded-full',
                  type === 'school' && 'srd:bg-main-blue-100 srd:text-neutrals-white',
                  type === 'web' && 'srd:bg-main-blue-20 srd:text-main-blue-100',
                )}
              >
                <RefreshCw className='srd:w-4 srd:h-4 srd:mr-2' />
                Coba Lagi
              </Button>
            </div>
          )
        }

        if (isPending) {
          return (
            <section className='srd:w-full srd:grid srd:2xl:grid-cols-6 srd:xl:grid-cols-5 srd:lg:grid-cols-4 srd:md:grid-cols-3 srd:grid-cols-2 srd:gap-4'>
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton
                  key={`love-books-skeleton-${index}`}
                  className='srd:w-full srd:h-[225px] srd:aspect-3/4 srd:rounded-md'
                />
              ))}
            </section>
          )
        }

        if (allRows.length === 0) {
          if (debouncedSearchTerm) {
            return <NoResultFound type={type} keyword={debouncedSearchTerm} />
          }
          return <Empty type={type} renderLink={renderEmptyLink} />
        }

        return (
          <section className='srd:w-full srd:flex srd:flex-col srd:gap-y-6'>
            <div className='srd:w-full srd:grid srd:2xl:grid-cols-6 srd:xl:grid-cols-5 srd:lg:grid-cols-4 srd:md:grid-cols-3 srd:grid-cols-2 srd:gap-4'>
              <AnimatePresence>
                {allRows.map((item, index) => (
                  <motion.div
                    key={`book-result-${item.book.id}`}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className='srd:w-full srd:h-fit srd:flex srd:items-center srd:justify-center'
                  >
                    <BookModal data={item.book} withAnimation={false}>
                      <Button className='srd:w-full' asChild>
                        {renderBookLink({
                          children: (
                            <>
                              <ReadBookIcon className='srd:size-6 srd:mr-2 srd:shrink-0' />
                              Baca buku
                            </>
                          ),
                          meta: { bookId: item.book.id, bookSlug: item.book.slug },
                        })}
                      </Button>
                    </BookModal>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {hasNextPage && (
              <motion.div
                className='srd:col-span-full srd:flex srd:justify-center srd:mt-4'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className={cn(type === 'web' && 'srd:bg-main-blue-20 srd:text-main-blue-100')}
                  loading={isFetchingNextPage}
                >
                  Muat lebih banyak <ChevronDown className='srd:w-4 srd:h-4 srd:ml-2' />
                </Button>
              </motion.div>
            )}
          </section>
        )
      })()}
    </main>
  )
}
